// HU-32b (MG-ILE-915) · tarea 32b-6. Administración › Usuarios.
//
// Spec `05_analisis_tecnico/Farmacovigilancia/ui/MG-ILE-915.md`. Lo que esta pantalla hace:
//
//   · LISTA a las personas con lo que el servidor ya derivó: el nombre de cada rol con su ámbito
//     («Representante del País · Bolivia»), el permiso L2, el estado, «Usuario externo» y su
//     invitación, y las acciones de cada fila (`acciones_permitidas`). Nada se traduce aquí.
//   · ALTA y EDICIÓN de roles en un `Modal`: una persona puede tener VARIOS roles y el ámbito es
//     POR ROL (decisión del operador). El formulario no sabe de reglas de ámbito: las lee del
//     catálogo de roles (`GET /administracion/roles`: `admite_todos_los_paises`,
//     `minimo/maximo_de_paises`, `texto_de_la_regla`, `asignable`).
//   · El Especialista invitado se VE pero no se asigna aquí (nace de la invitación al caso, P-19).
//     Si la persona ya lo tiene, su fila sale en solo lectura con `texto_de_no_asignable` y se
//     REENVÍA tal cual en el PUT: omitirlo sería quitarlo a mano y el servidor responde 422.
//   · PERMISO L2 con motivo (C5) y DESACTIVAR con confirmación (C4), cada uno en su `Modal`.
//   · Tras cada acción, la lista se vuelve a pedir al servidor (§8.1 R3).
//
// Textos de botones y mensajes sin criterio que los fije (V-06 de la spec): son los nombres que la
// spec da a cada acción; los mensajes de campo y de éxito son propuesta y están en el checklist.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { KeyRound, Pencil, Plus, Power, PowerOff, RotateCcw, ShieldOff, Trash2, UserPlus, Users } from 'lucide-react';
import {
  Alert, Button, Checkbox, DataTable, EmptyState, Field, Input, Modal, PageHeader, RoleChip, Select, StatusBadge,
  Textarea,
} from '../components/ui';
import type { ColumnaDeTabla, FilaDeTabla } from '../components/ui';
import Bandera from '../components/Banderas';
import {
  cambiarEstado, cambiarPermisoL2, cambiarRoles, darDeAlta, listarRoles, listarUsuarios,
} from '../api/usuarios';
import type {
  AsignacionDeRolEntrada, ListaDeUsuarios, RolDelCatalogo, UsuarioDelSistema,
} from '../api/usuarios';
import { listarPaises } from '../api/catalogos';
import { aFallo, MENSAJE_CORREO_INVALIDO } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import { esCorreoValido } from '../lib/correo';
import { tonoDelEstadoDelUsuario } from '../lib/etiquetas';
import { tonoDelRol } from '../data/navegacion';
import { useErroresAlSalir } from '../lib/useErroresAlSalir';
import { puedeDescartar } from '../lib/descarte';

const SIN_ACCESO = 'No tienes acceso a esta opción';
/** HU-32b-C2, literal del criterio. */
const MENSAJE_UN_PAIS = 'El Representante del País debe tener exactamente un país';

type Pais = { codigo: string; nombre: string };

/** Una fila del formulario «Roles y ámbito». `fija` = solo lectura (no asignable desde aquí). */
type FilaDeRol = { clave: number; rol: string; paises: string[]; corporativo: boolean; fija: boolean };

let siguienteClave = 1;
const filaNueva = (): FilaDeRol => ({ clave: siguienteClave++, rol: '', paises: [], corporativo: false, fija: false });

// ---------------------------------------------------------------------------------
// Formulario de alta y edición
// ---------------------------------------------------------------------------------

function FilaDeAsignacion({ fila, numero, roles, paises, rolDe, error, onCambiar, onQuitar, onTocar }: {
  fila: FilaDeRol;
  numero: number;
  roles: RolDelCatalogo[];
  paises: Pais[];
  rolDe: (codigo: string) => RolDelCatalogo | undefined;
  error: (campo: string) => string | undefined;
  onCambiar: (fila: FilaDeRol) => void;
  onQuitar: () => void;
  onTocar: (campo: string) => void;
}) {
  const definicion = rolDe(fila.rol);
  const campoRol = `rol-${fila.clave}`;
  const campoPaises = `paises-${fila.clave}`;

  if (fila.fija) {
    // Especialista invitado (o un rol que ya no se asigna): se ve y viaja tal cual, sin controles.
    return (
      <div data-testid="usuarios-form-asignacion" className="rounded-xl border border-borde p-3 bg-canvas/50">
        <div className="flex flex-wrap items-center gap-2">
          <RoleChip tone={tonoDelRol(fila.rol)}>{definicion?.nombre ?? fila.rol}</RoleChip>
          {definicion?.texto_de_la_regla && <span className="text-meta text-ink-soft">{definicion.texto_de_la_regla}</span>}
        </div>
        {definicion?.texto_de_no_asignable && (
          <p data-testid="usuarios-form-no-asignable" className="text-meta text-ink-soft mt-2">{definicion.texto_de_no_asignable}</p>
        )}
      </div>
    );
  }

  const fijo = definicion && definicion.maximo_de_paises === 0;
  const elegirPais = (codigo: string, marcado: boolean) => {
    const conjunto = new Set(fila.paises);
    if (marcado) conjunto.add(codigo); else conjunto.delete(codigo);
    onCambiar({ ...fila, paises: paises.map((p) => p.codigo).filter((c) => conjunto.has(c)) });
    onTocar(campoPaises);
  };

  return (
    <div data-testid="usuarios-form-asignacion" className="rounded-xl border border-borde p-3 space-y-3">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0 md:max-w-[50%]">
          <Field label={`Rol ${numero}`} required error={error(campoRol)} onTocar={() => onTocar(campoRol)} campo={campoRol}>
            <Select data-testid="usuarios-form-rol" value={fila.rol} sinVacio placeholder="Elige el rol..."
              onChange={(e) => {
                onCambiar({ ...fila, rol: String(e.target.value ?? ''), paises: [], corporativo: false });
                onTocar(campoRol);
              }}
              options={roles.filter((r) => r.asignable).map((r) => ({ value: r.codigo, label: r.nombre, hint: r.texto_de_la_regla }))} />
          </Field>
        </div>
        <span className="mt-6 shrink-0">
          <Button size="sm" variant="ghost" icon={Trash2} onClick={onQuitar} data-testid="usuarios-form-quitar-rol"
            aria-label={`Quitar el rol ${numero}`}>
            Quitar rol
          </Button>
        </span>
      </div>

      {definicion && fijo && (
        <div>
          <p className="block font-semibold text-meta text-ink-soft mb-1">Ámbito</p>
          <p data-testid="usuarios-form-ambito-fijo" className="text-body text-ink">{definicion.texto_de_la_regla}</p>
        </div>
      )}

      {definicion && !fijo && (
        <Field label="Ámbito: países" grupo required={definicion.minimo_de_paises > 0} hint={definicion.texto_de_la_regla}
          error={error(campoPaises)} campo={campoPaises} data-testid="usuarios-form-paises">
          <div className="space-y-2">
            {definicion.admite_todos_los_paises && (
              <Checkbox label="Todos los países" checked={fila.corporativo} data-testid="usuarios-form-corporativo"
                onChange={(e) => { onCambiar({ ...fila, corporativo: e.target.checked, paises: [] }); onTocar(campoPaises); }} />
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {paises.map((p) => (
                <Checkbox key={p.codigo} disabled={fila.corporativo} checked={fila.paises.includes(p.codigo)}
                  data-testid={`usuarios-form-paises-${p.codigo.toLowerCase()}`}
                  label={<span className="inline-flex items-center gap-1.5"><Bandera cod={p.codigo} nombre={p.nombre} />{p.nombre}</span>}
                  onChange={(e) => elegirPais(p.codigo, e.target.checked)} />
              ))}
            </div>
          </div>
        </Field>
      )}
    </div>
  );
}

/** Alta: el nombre y el correo de la persona (el cargo es opcional). */
function erroresDeLosDatos(nombre: string, correo: string): Record<string, string | undefined> {
  return {
    nombre: nombre.trim() ? undefined : 'Escribe el nombre de la persona.',
    correo: esCorreoValido(correo.trim()) ? undefined : MENSAJE_CORREO_INVALIDO,
  };
}

/** El rol de una fila: obligatorio y sin repetir el de otra fila. */
function errorDelRol(rol: string, usados: ReadonlySet<string>): string | undefined {
  if (!rol) return 'Elige el rol.';
  if (usados.has(rol)) return 'Ese rol ya está en otra fila.';
  return undefined;
}

/** Las reglas de cada fila de rol que se puede editar (las fijas no se validan aquí). */
function erroresDeLasFilas(filas: FilaDeRol[], roles: RolDelCatalogo[]): Record<string, string | undefined> {
  const errores: Record<string, string | undefined> = {};
  const usados = new Set<string>();
  for (const f of filas) {
    if (f.fija) continue;
    errores[`rol-${f.clave}`] = errorDelRol(f.rol, usados);
    if (f.rol) usados.add(f.rol);
    const definicion = roles.find((r) => r.codigo === f.rol);
    // HU-32b-C2: el Representante del País tiene exactamente un país. Las demás reglas de ámbito
    // las vuelve a exigir el servidor y su `detalle` se muestra en el aviso del cuadro.
    errores[`paises-${f.clave}`] = definicion?.regla_de_ambito === 'un_pais' && f.paises.length !== 1 ? MENSAJE_UN_PAIS : undefined;
  }
  return errores;
}

type ModoDelFormulario = { modo: 'alta' } | { modo: 'edicion'; usuario: UsuarioDelSistema };

function FormularioDeUsuario({ abierto, modo, roles, paises, onCerrar, onGuardado }: {
  abierto: boolean;
  modo: ModoDelFormulario | null;
  roles: RolDelCatalogo[];
  paises: Pais[];
  onCerrar: () => void;
  onGuardado: (mensaje: string) => void;
}) {
  const [nombre, setNombre] = useState('');
  const [correo, setCorreo] = useState('');
  const [cargo, setCargo] = useState('');
  const [filas, setFilas] = useState<FilaDeRol[]>([]);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [errorDelCorreo, setErrorDelCorreo] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const inicial = useRef('');

  const rolDe = useCallback((codigo: string) => roles.find((r) => r.codigo === codigo), [roles]);
  const edicion = modo?.modo === 'edicion' ? modo.usuario : null;

  // Se reinicia al ABRIR, no en cada render del padre.
  useEffect(() => {
    if (!abierto) return;
    setFallo(null);
    setErrorDelCorreo(null);
    if (edicion) {
      setNombre(edicion.nombre);
      setCorreo(edicion.correo);
      setCargo(edicion.cargo ?? '');
      const actuales = edicion.asignaciones.map((a): FilaDeRol => {
        const definicion = roles.find((r) => r.codigo === a.rol);
        return {
          clave: siguienteClave++,
          rol: a.rol,
          paises: a.paises,
          corporativo: Boolean(definicion?.admite_todos_los_paises) && a.paises.length === 0,
          fija: !definicion?.asignable,
        };
      });
      setFilas(actuales);
      inicial.current = JSON.stringify(actuales.map(({ rol, paises: p, corporativo }) => [rol, p, corporativo]));
    } else {
      setNombre(''); setCorreo(''); setCargo('');
      setFilas([filaNueva()]);
      inicial.current = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al abrir
  }, [abierto]);

  // Las reglas de cada campo, recalculadas en cada render. Los datos de la persona solo se
  // validan en el alta: en la edición se leen, no se escriben.
  const errores: Record<string, string | undefined> = {
    ...(edicion ? {} : erroresDeLosDatos(nombre, correo)),
    ...erroresDeLasFilas(filas, roles),
  };
  const { errorDe, tocar, tocarTodo, reiniciar } = useErroresAlSalir(errores);
  useEffect(() => { if (abierto) reiniciar(); }, [abierto, reiniciar]);

  const actuales = JSON.stringify(filas.filter((f) => f.rol).map(({ rol, paises: p, corporativo }) => [rol, p, corporativo]));
  const sucio = edicion ? actuales !== inicial.current : Boolean(nombre || correo || cargo || filas.some((f) => f.rol));

  const cancelar = () => { if (puedeDescartar(sucio)) onCerrar(); };
  const cambiarFila = (nueva: FilaDeRol) => setFilas((todas) => todas.map((x) => (x.clave === nueva.clave ? nueva : x)));
  const quitarFila = (clave: number) => setFilas((todas) => todas.filter((x) => x.clave !== clave));

  const asignaciones = (): AsignacionDeRolEntrada[] => filas
    .filter((f) => f.rol)
    .map((f) => {
      const definicion = rolDe(f.rol);
      if (f.fija) return { rol: f.rol, paises: f.paises.length ? f.paises : null };
      if (!definicion || definicion.maximo_de_paises === 0 || f.corporativo) return { rol: f.rol, paises: null };
      return { rol: f.rol, paises: f.paises };
    });

  const guardar = async () => {
    if (!tocarTodo()) return;
    setGuardando(true);
    setFallo(null);
    setErrorDelCorreo(null);
    try {
      if (edicion) {
        const actualizado = await cambiarRoles(edicion.usuario_id, asignaciones());
        onGuardado(`Roles y ámbito de ${actualizado.nombre} actualizados.`);
      } else {
        const creado = await darDeAlta({
          nombre: nombre.trim(), correo: correo.trim(), cargo: cargo.trim() || null, asignaciones: asignaciones(),
        });
        onGuardado(`${creado.nombre} quedó dado de alta.`);
      }
    } catch (e) {
      const f = aFallo(e);
      // 409 del correo ya dado de alta: bajo el campo, con el texto del servidor.
      if (f.cuerpo?.codigo === 'usuario_ya_registrado') setErrorDelCorreo(f.message);
      else setFallo(f);
    } finally {
      setGuardando(false);
    }
  };

  const titulo = edicion ? `Editar roles y ámbito · ${edicion.nombre}` : 'Dar de alta a una persona';

  return (
    <Modal open={abierto} onClose={onCerrar} sucio={sucio} ancho="xl" data-testid="usuarios-form"
      eyebrow="Administración · Usuarios" title={titulo} icon={edicion ? Pencil : UserPlus}
      footer={(
        <>
          <Button variant="secondary" onClick={cancelar} data-testid="usuarios-form-cancelar">Cancelar</Button>
          <Button onClick={() => { guardar(); }} disabled={guardando} data-testid="usuarios-form-guardar">
            {guardando ? 'Guardando…' : (edicion ? 'Guardar' : 'Dar de alta')}
          </Button>
        </>
      )}>
      <div className="space-y-5">
        {fallo && (
          <Alert tone="danger" title="No se pudo guardar" data-testid="usuarios-error-envio" detalleTecnico={fallo.detalleTecnico}>
            {fallo.message}
          </Alert>
        )}

        <section aria-labelledby="usuarios-datos" className="space-y-3">
          <h3 id="usuarios-datos" className="text-body font-semibold text-ink">Datos de la persona</h3>
          {edicion ? (
            <dl className="grid grid-cols-1 md:grid-cols-3 gap-3 text-body">
              <div><dt className="text-meta text-ink-soft font-semibold">Nombre</dt><dd data-testid="usuarios-form-nombre">{edicion.nombre}</dd></div>
              <div><dt className="text-meta text-ink-soft font-semibold">Correo</dt><dd data-testid="usuarios-form-correo" className="break-all">{edicion.correo}</dd></div>
              <div><dt className="text-meta text-ink-soft font-semibold">Cargo</dt><dd data-testid="usuarios-form-cargo">{edicion.cargo ?? ''}</dd></div>
            </dl>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Nombre" required error={errorDe('nombre')} onTocar={() => tocar('nombre')} campo="nombre">
                <Input data-testid="usuarios-form-nombre" value={nombre} maxLength={200} autoComplete="off"
                  onChange={(e) => setNombre(e.target.value)} />
              </Field>
              <Field label="Correo" required error={errorDelCorreo ?? errorDe('correo')} onTocar={() => tocar('correo')} campo="correo"
                hint="Si no es de ILENDER, se envía la invitación de Microsoft.">
                <Input data-testid="usuarios-form-correo" type="email" value={correo} maxLength={320} autoComplete="off"
                  onChange={(e) => { setCorreo(e.target.value); setErrorDelCorreo(null); }} />
              </Field>
              <Field label="Cargo" hint="Es el que registra la constancia de aprobación.">
                <Input data-testid="usuarios-form-cargo" value={cargo} maxLength={200} autoComplete="off"
                  onChange={(e) => setCargo(e.target.value)} />
              </Field>
            </div>
          )}
        </section>

        <section aria-labelledby="usuarios-roles" className="space-y-3">
          <h3 id="usuarios-roles" className="text-body font-semibold text-ink">Roles y ámbito</h3>
          {filas.map((f, i) => (
            <FilaDeAsignacion key={f.clave} fila={f} numero={i + 1} roles={roles} paises={paises} rolDe={rolDe}
              error={errorDe} onTocar={tocar}
              onCambiar={cambiarFila} onQuitar={() => quitarFila(f.clave)} />
          ))}
          {!filas.length && (
            <p className="text-meta text-ink-soft">
              Sin roles, la persona queda «Sin acceso» hasta que se le asigne uno.
            </p>
          )}
          <Button variant="secondary" size="sm" icon={Plus} onClick={() => setFilas((todas) => [...todas, filaNueva()])}
            data-testid="usuarios-form-agregar-rol">
            Agregar otro rol
          </Button>
          {/* HU-32b-C6: el catálogo tiene 8 roles y se asignan 7. El que no se asigna desde aquí se
              nombra, con el motivo que da el servidor, para que no parezca que falta. */}
          {roles.filter((r) => !r.asignable).map((r) => (
            <p key={r.codigo} data-testid="usuarios-form-rol-no-asignable" className="text-meta text-ink-soft">
              <b className="font-semibold text-ink">{r.nombre}</b>: {r.texto_de_no_asignable}
            </p>
          ))}
        </section>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------------
// Permiso L2 y desactivar
// ---------------------------------------------------------------------------------

function ModalPermisoL2({ usuario, onCerrar, onGuardado }: {
  usuario: UsuarioDelSistema | null;
  onCerrar: () => void;
  onGuardado: (mensaje: string) => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [guardando, setGuardando] = useState(false);
  const conceder = Boolean(usuario?.acciones_permitidas.includes('conceder_l2'));
  const texto = motivo.trim();
  const errores = {
    motivo: !texto ? 'Escribe el motivo: sin motivo no se guarda.'
      : (texto.length < 5 ? 'El motivo necesita al menos 5 caracteres.' : undefined),
  };
  const { errorDe, tocar, tocarTodo, reiniciar } = useErroresAlSalir(errores);
  useEffect(() => { setMotivo(''); setFallo(null); reiniciar(); }, [usuario, reiniciar]);

  const confirmar = async () => {
    if (!usuario || !tocarTodo()) return;
    setGuardando(true);
    setFallo(null);
    try {
      const actualizado = await cambiarPermisoL2(usuario.usuario_id, conceder, texto);
      onGuardado(`${actualizado.nombre}: ${actualizado.texto_del_permiso_l2}.`);
    } catch (e) {
      setFallo(aFallo(e));
    } finally {
      setGuardando(false);
    }
  };

  const accion = conceder ? 'Conceder permiso L2' : 'Revocar permiso L2';
  return (
    <Modal open={Boolean(usuario)} onClose={onCerrar} sucio={Boolean(motivo)} tone="warning" icon={KeyRound}
      data-testid="usuarios-modal-l2" eyebrow="Administración · Usuarios" title={accion}
      footer={(
        <>
          <Button variant="secondary" onClick={() => { if (puedeDescartar(Boolean(motivo))) onCerrar(); }} data-testid="usuarios-form-cancelar">Cancelar</Button>
          <Button onClick={() => { confirmar(); }} disabled={guardando} data-testid="usuarios-confirmar-l2">
            {guardando ? 'Guardando…' : accion}
          </Button>
        </>
      )}>
      <div className="space-y-4">
        <p data-testid="usuarios-form-l2-accion" className="text-body">
          {conceder ? 'Conceder' : 'Revocar'} el permiso L2 a <b>{usuario?.nombre}</b>.
        </p>
        {fallo && <Alert tone="danger" title="No se pudo guardar" data-testid="usuarios-error-envio" detalleTecnico={fallo.detalleTecnico}>{fallo.message}</Alert>}
        <Field label="Motivo" required error={errorDe('motivo')} onTocar={() => tocar('motivo')} campo="motivo"
          contador={`${motivo.length} / 1000`} hint="Queda en la pista de auditoría con tu nombre, la fecha y la hora.">
          <Textarea data-testid="usuarios-form-motivo" rows={3} maxLength={1000} value={motivo}
            onChange={(e) => setMotivo(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

function ModalDesactivar({ usuario, onCerrar, onGuardado }: {
  usuario: UsuarioDelSistema | null;
  onCerrar: () => void;
  onGuardado: (mensaje: string) => void;
}) {
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [guardando, setGuardando] = useState(false);
  useEffect(() => { setFallo(null); }, [usuario]);

  const confirmar = async () => {
    if (!usuario) return;
    setGuardando(true);
    setFallo(null);
    try {
      const actualizado = await cambiarEstado(usuario.usuario_id, false);
      onGuardado(`${actualizado.nombre}: ${actualizado.texto_del_estado}.`);
    } catch (e) {
      setFallo(aFallo(e));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal open={Boolean(usuario)} onClose={onCerrar} tone="danger" icon={PowerOff} data-testid="usuarios-modal-desactivar"
      eyebrow="Administración · Usuarios" title={`¿Desactivar a ${usuario?.nombre ?? ''}?`}
      footer={(
        <>
          <Button variant="secondary" onClick={onCerrar} data-testid="usuarios-form-cancelar">Cancelar</Button>
          {/* Primario y no `danger`: el blanco sobre rose-500 del catálogo no llega a 4,5:1 (lineamiento, par 4); el tono del cuadro ya dice que es una baja. */}
          <Button onClick={() => { confirmar(); }} disabled={guardando} data-testid="usuarios-confirmar-desactivar">
            {guardando ? 'Desactivando…' : 'Sí, desactivar'}
          </Button>
        </>
      )}>
      <div className="space-y-3">
        <p>{usuario?.nombre} pierde el acceso en su siguiente petición: verá que su cuenta no tiene acceso asignado.</p>
        <p className="text-ink-soft">No se borra: los casos y la pista de auditoría lo siguen nombrando. Se puede reactivar.</p>
        {fallo && <Alert tone="danger" title="No se pudo desactivar" data-testid="usuarios-error-envio" detalleTecnico={fallo.detalleTecnico}>{fallo.message}</Alert>}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------------
// La pantalla
// ---------------------------------------------------------------------------------

const COLUMNAS: ColumnaDeTabla[] = [
  { key: 'persona', label: 'Nombre y cargo', className: 'w-[20%]' },
  { key: 'correo', label: 'Correo', className: 'w-[22%]' },
  { key: 'roles', label: 'Roles y ámbito', className: 'w-[28%]' },
  { key: 'l2', label: 'Permiso L2' },
  { key: 'estado', label: 'Estado' },
  { key: 'acciones', label: '' },
];

export default function Usuarios() {
  const [lista, setLista] = useState<ListaDeUsuarios | null>(null);
  const [roles, setRoles] = useState<RolDelCatalogo[]>([]);
  const [paises, setPaises] = useState<Pais[]>([]);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [falloDeAccion, setFalloDeAccion] = useState<Fallo | null>(null);
  const [exito, setExito] = useState<string | null>(null);
  const [formulario, setFormulario] = useState<ModoDelFormulario | null>(null);
  const [l2De, setL2De] = useState<UsuarioDelSistema | null>(null);
  const [desactivarA, setDesactivarA] = useState<UsuarioDelSistema | null>(null);
  const [reactivando, setReactivando] = useState<string | null>(null);

  const vivo = useRef(true);
  useEffect(() => {
    vivo.current = true;
    return () => { vivo.current = false; };
  }, []);

  const cargar = useCallback(async () => {
    setCargando(true);
    setFallo(null);
    try {
      const [usuarios, catalogo, listaDePaises] = await Promise.all([listarUsuarios(), listarRoles(), listarPaises()]);
      if (!vivo.current) return;
      setLista(usuarios);
      setRoles(catalogo);
      setPaises((listaDePaises ?? [])
        .filter((p) => p.activo !== false)
        .map((p) => ({ codigo: String(p.codigo), nombre: String(p.nombre ?? p.codigo) })));
    } catch (e) {
      if (vivo.current) setFallo(aFallo(e));
    } finally {
      if (vivo.current) setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  /** Tras cada acción: el aviso de éxito y la lista vuelta a pedir al servidor (§8.1 R3). */
  const hecho = (mensaje: string) => {
    setFormulario(null);
    setL2De(null);
    setDesactivarA(null);
    setFalloDeAccion(null);
    setExito(mensaje);
    cargar();
  };

  const reactivar = async (u: UsuarioDelSistema) => {
    setExito(null);
    setFalloDeAccion(null);
    setReactivando(u.usuario_id);
    try {
      const actualizado = await cambiarEstado(u.usuario_id, true);
      hecho(`${actualizado.nombre}: ${actualizado.texto_del_estado}.`);
    } catch (e) {
      setFalloDeAccion(aFallo(e));
    } finally {
      setReactivando(null);
    }
  };

  const abrir = (accion: () => void) => { setExito(null); setFalloDeAccion(null); accion(); };
  const puedeDarDeAlta = Boolean(lista?.acciones_permitidas.includes('alta'));

  const encabezado = (
    <PageHeader
      eyebrow="Administración · Usuarios"
      hu="HU-32b · RN-58 / RN-59 / RN-60 / RN-61"
      title="Usuarios"
      subtitle="Quién entra al sistema, con qué roles y en qué países. Una persona puede tener varios roles; el ámbito es de cada rol."
      icon={Users}
      actions={puedeDarDeAlta && !fallo ? (
        <Button icon={UserPlus} onClick={() => abrir(() => setFormulario({ modo: 'alta' }))} data-testid="usuarios-alta">
          Dar de alta
        </Button>
      ) : undefined}
    />
  );

  if (fallo?.estado === 403) {
    return (
      <>
        {encabezado}
        <EmptyState icon={ShieldOff} title={SIN_ACCESO} data-testid="usuarios-sin-permiso"
          description="Los usuarios y sus roles los administra el administrador del sistema." />
      </>
    );
  }

  const celda = (fila: FilaDeTabla, columna: ColumnaDeTabla): ReactNode => {
    const u = fila as unknown as UsuarioDelSistema;
    switch (columna.key) {
      case 'persona':
        return (
          <span className="block min-w-0">
            <span className="block font-semibold text-ink">{u.nombre}</span>
            {u.cargo && <span className="block text-meta text-ink-soft">{u.cargo}</span>}
          </span>
        );
      case 'correo':
        return (
          <span className="block min-w-0 space-y-1">
            <span data-testid="usuarios-tabla-correo" className="block break-all">{u.correo}</span>
            {u.es_externo && u.texto_del_tipo && (
              <StatusBadge tone="info" data-testid="usuarios-tabla-externo">{u.texto_del_tipo}</StatusBadge>
            )}
            {u.invitacion && <span data-testid="usuarios-tabla-invitacion" className="block text-meta text-ink-soft">{u.invitacion.texto}</span>}
          </span>
        );
      case 'roles':
        return (
          <ul data-testid="usuarios-tabla-roles" className="space-y-1">
            {u.asignaciones.map((a) => (
              <li key={a.rol} className="flex flex-wrap items-center gap-1.5">
                <RoleChip tone={tonoDelRol(a.rol)}>{a.texto_del_rol}</RoleChip>
                <span className="text-meta text-ink-soft">{a.texto_del_ambito}</span>
              </li>
            ))}
          </ul>
        );
      case 'l2':
        return <StatusBadge tone={u.permiso_l2 ? 'warning' : 'neutral'} data-testid="usuarios-tabla-l2">{u.texto_del_permiso_l2}</StatusBadge>;
      case 'estado':
        return <StatusBadge tone={tonoDelEstadoDelUsuario(u.estado)} data-testid="usuarios-tabla-estado">{u.texto_del_estado}</StatusBadge>;
      case 'acciones':
        return (
          <span className="flex flex-wrap justify-end gap-1.5">
            {u.acciones_permitidas.includes('editar_roles') && (
              <Button size="sm" variant="ghost" icon={Pencil} data-testid="usuarios-tabla-editar"
                aria-label={`Editar roles y ámbito de ${u.nombre}`}
                onClick={() => abrir(() => setFormulario({ modo: 'edicion', usuario: u }))}>
                Editar roles y ámbito
              </Button>
            )}
            {u.acciones_permitidas.includes('conceder_l2') && (
              <Button size="sm" variant="ghost" icon={KeyRound} data-testid="usuarios-tabla-conceder-l2"
                aria-label={`Conceder permiso L2 a ${u.nombre}`} onClick={() => abrir(() => setL2De(u))}>
                Conceder permiso L2
              </Button>
            )}
            {u.acciones_permitidas.includes('revocar_l2') && (
              <Button size="sm" variant="ghost" icon={KeyRound} data-testid="usuarios-tabla-revocar-l2"
                aria-label={`Revocar permiso L2 a ${u.nombre}`} onClick={() => abrir(() => setL2De(u))}>
                Revocar permiso L2
              </Button>
            )}
            {u.acciones_permitidas.includes('desactivar') && (
              <Button size="sm" variant="ghost" icon={PowerOff} data-testid="usuarios-tabla-desactivar"
                aria-label={`Desactivar a ${u.nombre}`} onClick={() => abrir(() => setDesactivarA(u))}>
                Desactivar
              </Button>
            )}
            {u.acciones_permitidas.includes('reactivar') && (
              <Button size="sm" variant="ghost" icon={Power} data-testid="usuarios-tabla-reactivar"
                aria-label={`Reactivar a ${u.nombre}`} disabled={reactivando === u.usuario_id}
                onClick={() => { reactivar(u); }}>
                Reactivar
              </Button>
            )}
          </span>
        );
      default:
        return null;
    }
  };

  const usuarios = lista?.usuarios ?? [];

  return (
    <>
      {encabezado}

      {exito && (
        <div className="mb-4"><Alert tone="success" data-testid="usuarios-exito" onCerrar={() => setExito(null)}>{exito}</Alert></div>
      )}
      {falloDeAccion && (
        <div className="mb-4">
          <Alert tone="danger" title="No se pudo completar" data-testid="usuarios-error-envio" detalleTecnico={falloDeAccion.detalleTecnico}
            onCerrar={() => setFalloDeAccion(null)}>
            {falloDeAccion.message}
          </Alert>
        </div>
      )}

      {fallo && (
        <div className="space-y-3">
          <Alert tone="danger" title="No se pudo cargar la lista de usuarios" data-testid="usuarios-error" detalleTecnico={fallo.detalleTecnico}>
            {fallo.message}{fallo.estado === 0 ? ' Revisa tu conexión e intenta refrescar.' : ''}
          </Alert>
          <Button variant="secondary" icon={RotateCcw} onClick={() => { cargar(); }} data-testid="usuarios-reintentar">Reintentar</Button>
        </div>
      )}

      {!fallo && cargando && !lista && (
        <p data-testid="usuarios-cargando" role="status" className="text-body text-ink-soft">Cargando usuarios…</p>
      )}

      {!fallo && lista && (
        <DataTable columns={COLUMNAS} rows={usuarios.map((u) => ({ ...u, id: u.usuario_id }))} renderCell={celda}
          testidDeFila="usuarios-tabla-fila" data-testid="usuarios-tabla"
          empty={(
            <EmptyState icon={Users} data-testid="usuarios-vacio" title="Todavía no hay usuarios dados de alta"
              description="Da de alta a la primera persona y asígnale sus roles."
              action={puedeDarDeAlta ? (
                <Button icon={UserPlus} onClick={() => abrir(() => setFormulario({ modo: 'alta' }))}>Dar de alta</Button>
              ) : undefined} />
          )} />
      )}

      <FormularioDeUsuario abierto={formulario !== null} modo={formulario} roles={roles} paises={paises}
        onCerrar={() => setFormulario(null)} onGuardado={hecho} />
      <ModalPermisoL2 usuario={l2De} onCerrar={() => setL2De(null)} onGuardado={hecho} />
      <ModalDesactivar usuario={desactivarA} onCerrar={() => setDesactivarA(null)} onGuardado={hecho} />
    </>
  );
}
